import { editorItemKey } from './playlistEditorModel';
const nameOf = (x) => x.artist?.name || x.artistName || '';
const normalize = (name) => name.trim().toLocaleLowerCase();
const sourceIdOf = (x) => x.artist?.sourceId || x.artistSourceId;
const sourceOf = (x) => x.artist?.artistSource || x.artistSource || 'APPLE_MUSIC';
const fresh = () => ({ children: {}, loading: new Set(), errors: new Set() });
// One engine per editor/account. Reset cancels the entire old search tree, including prefetch.
export function createEditorRecommendations({
  readArtist,
  searchArtists,
  searchTracks,
  getSelected,
  onChange,
}) {
  let state = fresh(),
    type = 'TRACK',
    roots = [],
    generation = 0,
    controller = new AbortController();
  const pending = new Map(),
    progress = new Map(),
    listeners = new Map(),
    cache = new Map();
  const valid = (token) => token === generation && !controller.signal.aborted;
  const notify = () => onChange(state);
  const cached = (key, load) => {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.time < 600000) return hit.promise;
    const signal = controller.signal;
    const entry = { time: Date.now() };
    entry.promise = Promise.resolve()
      .then(() => {
        if (signal.aborted) throw Error('Cancelled');
        return load(signal);
      })
      .catch((error) => {
        if (cache.get(key) === entry) cache.delete(key);
        throw error;
      });
    cache.set(key, entry);
    return entry.promise;
  };
  const detail = (artist) =>
    cached(`artist:${artist.artistSource}:${artist.sourceId}`, (signal) =>
      readArtist(artist, signal),
    );
  const resolveArtist = async (item) => {
    if (sourceIdOf(item)) return { sourceId: sourceIdOf(item), artistSource: sourceOf(item) };
    const name = nameOf(item);
    if (!name) return null;
    const artists = await cached(`search-artist:${name}`, (signal) => searchArtists(name, signal));
    const match = artists.find((x) => normalize(x.name) === normalize(name)) || artists[0];
    return match
      ? { sourceId: match.sourceId ?? match.id, artistSource: match.artistSource || 'APPLE_MUSIC' }
      : null;
  };
  const representative = async (artist, forbidden, token) => {
    if (!valid(token)) return;
    let item;
    if (type === 'ALBUM') {
      const data = await detail(artist);
      if (!valid(token)) return;
      item = [...(data.fullAlbums?.items || []), ...(data.singles?.items || [])].find(
        (x) => !forbidden.has(editorItemKey(x)),
      );
    } else {
      const tracks = await cached(`tracks:${artist.name}`, (signal) =>
        searchTracks(artist.name, signal),
      );
      if (!valid(token)) return;
      item = tracks.find(
        (x) =>
          (String(sourceIdOf(x) || '') === String(artist.sourceId) ||
            normalize(nameOf(x)) === normalize(artist.name)) &&
          !forbidden.has(editorItemKey(x)),
      );
    }
    return item
      ? {
          ...item,
          albumImageUrl: item.albumImageUrl || item.imageUrl,
          artist: {
            name: artist.name,
            sourceId: artist.sourceId,
            artistSource: artist.artistSource,
          },
        }
      : undefined;
  };
  const outside = (key) =>
    new Set(
      Object.entries(state.children)
        .filter(([k]) => k !== key)
        .flatMap(([, list]) => list.map((x) => editorItemKey(x.item))),
    );
  const build = async (parent, depth, publish, extra = new Set()) => {
    const token = generation,
      artist = await resolveArtist(parent);
    if (!valid(token)) return [];
    if (!artist) throw Error('Artist missing');
    const data = await detail(artist);
    if (!valid(token)) return [];
    const forbidden = new Set([
      ...getSelected().map(editorItemKey),
      ...roots.map(editorItemKey),
      ...Object.values(state.children)
        .flat()
        .map((x) => editorItemKey(x.item)),
      ...extra,
      editorItemKey(parent),
    ]);
    const children = [];
    const add = (item, relation) => {
      if (!valid(token) || !item || forbidden.has(editorItemKey(item)) || children.length >= 2)
        return;
      forbidden.add(editorItemKey(item));
      children.push({ item, relation });
      publish([...children]);
    };
    if (depth > 0) add(await representative(data.artist, forbidden, token), 'SAME_ARTIST');
    const similar = (data.similarArtists?.items || [])
      .filter(
        (x, i, all) =>
          String(x.sourceId) !== String(artist.sourceId) &&
          all.findIndex((y) => String(y.sourceId) === String(x.sourceId)) === i,
      )
      .slice(0, 3);
    await Promise.all(
      similar.map(async (x) =>
        add(await representative(x, forbidden, token).catch(() => undefined), 'SIMILAR_ARTIST'),
      ),
    );
    return children;
  };
  const promiseFor = (parent, depth, listener) => {
    const key = `${editorItemKey(parent)}:${depth > 0 ? 'branch' : 'root'}`,
      token = generation;
    if (listener) {
      if (!listeners.has(key)) listeners.set(key, new Set());
      listeners.get(key).add(listener);
      if (progress.has(key)) listener(progress.get(key));
    }
    if (!pending.has(key)) {
      const publish = (value) => {
        if (!valid(token)) return;
        progress.set(key, value);
        listeners.get(key)?.forEach((fn) => fn(value));
      };
      const request = build(parent, depth, publish)
        .then((children) => {
          publish(children);
          return children;
        })
        .catch((error) => {
          if (valid(token)) {
            pending.delete(key);
            progress.delete(key);
          }
          throw error;
        })
        .finally(() => {
          if (valid(token)) listeners.delete(key);
        });
      pending.set(key, request);
    }
    return pending.get(key);
  };
  const prefetch = (parent, depth) => {
    if (parent.albumSource === 'APPLE_MUSIC') void promiseFor(parent, depth).catch(() => {});
  };
  const commit = (parent, children) => {
    const used = outside(editorItemKey(parent));
    const unique = children.filter((x) => {
      const key = editorItemKey(x.item);
      if (used.has(key)) return false;
      used.add(key);
      return true;
    });
    state = { ...state, children: { ...state.children, [editorItemKey(parent)]: unique } };
    notify();
    return unique;
  };
  const load = async (parent, depth = 0, force = false) => {
    const key = editorItemKey(parent),
      token = generation;
    if (
      controller.signal.aborted ||
      parent.albumSource !== 'APPLE_MUSIC' ||
      state.loading.has(key) ||
      (!force && state.children[key])
    )
      return;
    if (force) {
      const requestKey = `${key}:${depth > 0 ? 'branch' : 'root'}`;
      pending.delete(requestKey);
      progress.delete(requestKey);
      listeners.delete(requestKey);
    }
    state = {
      ...state,
      loading: new Set(state.loading).add(key),
      errors: new Set([...state.errors].filter((x) => x !== key)),
    };
    notify();
    const publish = (children) => {
      if (valid(token)) commit(parent, children).forEach((x) => prefetch(x.item, depth + 1));
    };
    try {
      const children = await promiseFor(parent, depth, publish);
      if (!valid(token)) return;
      const used = outside(key);
      const resolved = children.some((x) => used.has(editorItemKey(x.item)))
        ? await build(parent, depth, publish, used)
        : children;
      if (valid(token)) publish(resolved);
    } catch {
      if (valid(token)) {
        state = { ...state, errors: new Set(state.errors).add(key) };
        notify();
      }
    } finally {
      if (valid(token)) {
        state = { ...state, loading: new Set([...state.loading].filter((x) => x !== key)) };
        notify();
      }
    }
  };
  return {
    load,
    reset(nextType, nextRoots) {
      generation++;
      controller.abort();
      controller = new AbortController();
      pending.clear();
      progress.clear();
      listeners.clear();
      cache.clear();
      type = nextType;
      roots = nextRoots;
      state = fresh();
      notify();
      const names = new Set();
      roots.forEach((item) => {
        const name = normalize(nameOf(item));
        if (name && names.size < 2 && !names.has(name)) {
          names.add(name);
          prefetch(item, 0);
        }
      });
    },
    dispose() {
      generation++;
      controller.abort();
      pending.clear();
      listeners.clear();
      progress.clear();
      cache.clear();
    },
    snapshot: () => state,
  };
}
